import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { missionsApi, findingsApi, vexApi } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { RiskTrendChart } from '@/components/dashboard/RiskTrendChart'
import { useToast } from '@/hooks/useToast'
import {
  Target,
  ArrowLeft,
  Shield,
  Clock,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
} from 'lucide-react'

export function MissionDetailPage() {
  const { id } = useParams<{ id: string }>()
  // Note: useAuth is called to ensure auth state is initialized
  useAuth()
  const { toast } = useToast()

  const { data: mission, isLoading: missionLoading } = useQuery({
    queryKey: ['missions', id],
    queryFn: () => missionsApi.get(id!),
    enabled: !!id,
  })

  const { data: findings = [] } = useQuery({
    queryKey: ['findings', id],
    queryFn: () => findingsApi.list(id!),
    enabled: !!id,
  })

  const handleExportVex = async () => {
    if (!id) return
    try {
      const vexDoc = await vexApi.export(id)
      const blob = new Blob([JSON.stringify(vexDoc, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vex-${id.slice(0, 8)}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast({ title: 'VEX Exported', description: 'VEX document downloaded successfully.' })
    } catch (error) {
      console.error('VEX export failed:', error)
      toast({ title: 'Export Failed', description: 'Failed to export VEX document.', variant: 'destructive' })
    }
  }

  const handleExportCSV = () => {
    if (!findings.length) return
    const headers = ['CWE ID', 'Scanner', 'JFAC Score', 'Severity', 'File Path', 'Line Number']
    const rows = findings.map(f => [
      f.cwe_id,
      f.scanner_identity,
      f.jfac_risk.score.toFixed(2),
      f.jfac_risk.label,
      f.file_path || '',
      f.line_number?.toString() || '',
    ])
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `findings-${id?.slice(0, 8)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast({ title: 'CSV Exported', description: `${findings.length} findings exported to CSV.` })
  }

  const severityCounts = findings.reduce(
    (acc, f) => {
      const label = f.jfac_risk.label.toLowerCase()
      acc[label] = (acc[label] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  if (missionLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!mission) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-medium">Mission not found</h2>
        <Button asChild variant="link" className="mt-4">
          <Link to="/missions">Back to Missions</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/missions" className="hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" />
          Missions
        </Link>
        <span>/</span>
        <span className="text-foreground">{mission.mission_name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Target className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold">{mission.mission_name}</h1>
            <p className="text-muted-foreground mt-1">
              {mission.mission_objective || 'No objective specified'}
            </p>
            <div className="flex items-center gap-4 mt-2">
              <Badge variant={mission.status === 'active' ? 'default' : 'secondary'}>
                {mission.status}
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Created {new Date(mission.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} disabled={findings.length === 0}>
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={handleExportVex}>
            <FileText className="h-4 w-4 mr-2" />
            VEX
          </Button>
        </div>
      </div>

      {/* Risk Trend Chart - Phase 14 Enhancement */}
      {id && (
        <RiskTrendChart missionId={id} days={30} className="w-full" />
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Findings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{findings.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Critical
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-400">
              {severityCounts['critical'] || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">High</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-400">
              {severityCounts['high'] || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Medium/Low</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-400">
              {(severityCounts['medium'] || 0) + (severityCounts['low'] || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Findings table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Findings
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/softwareiq/assurance/findings?mission=${id}`}>View All</Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>CWE</th>
                  <th>Scanner</th>
                  <th>JFAC Score</th>
                  <th>Severity</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {findings.slice(0, 10).map((finding) => (
                  <tr key={finding.finding_id}>
                    <td className="font-mono text-primary">{finding.cwe_id}</td>
                    <td>{finding.scanner_identity}</td>
                    <td className="font-mono">{finding.jfac_risk.score.toFixed(2)}</td>
                    <td>
                      <Badge
                        variant={
                          finding.jfac_risk.label.toLowerCase() === 'critical' ? 'critical' :
                          finding.jfac_risk.label.toLowerCase() === 'high' ? 'high' :
                          finding.jfac_risk.label.toLowerCase() === 'medium' ? 'medium' : 'low'
                        }
                      >
                        {finding.jfac_risk.label}
                      </Badge>
                    </td>
                    <td className="font-mono text-xs text-muted-foreground">
                      {finding.file_path
                        ? `${finding.file_path}:${finding.line_number || '?'}`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {findings.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No findings for this mission yet.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
